<template>
    <div>
        <div v-for="scheduleType in scheduleTypes" :key="scheduleType.key" class="mt-8">
            <h5 class="mb-2">
                <KpIcon :icon-cls="scheduleType.icon" color="black" left />{{ $t(scheduleType.label) }}
            </h5>
            <div v-for="schedule in scheduleType.schedules" :key="schedule?.getId()">
                <ScheduleCard
                    :record="schedule"
                    :event="event"
                    class="mt-4"
                    @edit="$emit('edit', schedule)"
                    @duplicate="$emit('duplicate', schedule)"
                    @delete="deleteSchedule"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { errToStr } from '@/helpers/tools';
import { Event } from '@/models/Event';
import { Schedule, ScheduleCollection } from '@/models/Schedule';
import ScheduleCard from '@/components/schedule/Card';

/**
 * Shows a card list of Schedules assigned to a specific Event.
 * Used/Shown in the Event edit panel.
 *
 * emits:
 * - edit(schedule): When the edit btn is clicked: Editing a schedule is handled by the parent component.
 * - deleted(schedule): deleting a schedule is handled here, but we emit an event AFTER the deletion took place.
 */
export default {
    name: 'ScheduleCardList',
    components: { ScheduleCard },
    props: {
        event: {
            type: Event,
            required: true,
        },
    },
    data() {
        const scheduleCollection = new ScheduleCollection();
        return {
            scheduleCollection,
        };
    },
    computed: {
        scheduleTypes() {
            const config = [
                {
                    schedules: [],
                    key: Schedule.RECURRING_TYPE_SINGLE,
                    icon: 'calendar',
                    label: 'recurring_type_single_pl',
                },
                {
                    schedules: [],
                    key: Schedule.RECURRING_TYPE_DAILY,
                    icon: 'events',
                    label: 'recurring_type_daily_pl',
                },
                {
                    schedules: [],
                    key: Schedule.RECURRING_TYPE_WEEKLY,
                    icon: 'events',
                    label: 'recurring_type_weekly_pl',
                },
                {
                    schedules: [],
                    key: Schedule.RECURRING_TYPE_MONTHLY,
                    icon: 'events',
                    label: 'recurring_type_monthly_pl',
                },
                {
                    schedules: [],
                    key: Schedule.RECURRING_TYPE_YEARLY,
                    icon: 'events',
                    label: 'recurring_type_yearly_pl',
                },
            ];

            for (const schedule of this.scheduleCollection.models) {
                switch (schedule.recurring_type) {
                    case Schedule.RECURRING_TYPE_SINGLE:
                        config[0].schedules.push(schedule);
                        break;
                    case Schedule.RECURRING_TYPE_DAILY:
                        config[1].schedules.push(schedule);
                        break;
                    case Schedule.RECURRING_TYPE_WEEKLY:
                        config[2].schedules.push(schedule);
                        break;
                    case Schedule.RECURRING_TYPE_MONTHLY:
                        config[3].schedules.push(schedule);
                        break;
                    case Schedule.RECURRING_TYPE_YEARLY:
                        config[4].schedules.push(schedule);
                        break;
                }
            }

            return config.filter((item) => item.schedules.length > 0);
        },
    },
    watch: {
        event: {
            handler(newValue, oldValue) {
                if (newValue?.getId() !== oldValue?.getId()) {
                    this.loadSchedules();
                }
            },
            deep: true,
        },
    },
    async mounted() {
        await this.loadSchedules();
    },

    methods: {
        async loadSchedules() {
            this.scheduleCollection.addExtraParam('filter', [{ property: 'event_id', value: this.event.getId() }]);
            await this.scheduleCollection.query();
        },
        async deleteSchedule(schedule) {
            if (!schedule) {
                return;
            }

            try {
                await schedule.delete();
                this.$emit('deleted', schedule);
            } catch (e) {
                this.$toast({ message: errToStr(e), color: 'error' });
            }
        },
    },
};
</script>

<style></style>
